import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { Loader2 } from 'lucide-react';
import FilterBuilder from '../../../components/FilterBuilder';
import AppContext from '../../../contexts/AppContext';

export const QuotaCreateDialog = ({ open, onClose, onQuotaCreated }) => {
  const { t } = useTranslation();
  const { serviceDesk, currentUser } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serviceDesksWithRequestTypes, setServiceDesksWithRequestTypes] = useState([]);

  const isSuperAdmin = currentUser?.role === 'superadmin';

  // Fetch service desks with request types
  React.useEffect(() => {
    if (open) {
      fetchWithAuth('/service-desks/with-request-types')
        .then(res => res.json())
        .then(json => {
          if (json.success) {
            setServiceDesksWithRequestTypes(json.data);
          }
        })
        .catch(err => console.error('Error fetching service desks with request types:', err));
    }
  }, [open]);

  // Get default service desk ID from current context
  const defaultServiceDeskId = serviceDesk?.id?.toString() || '';

  const [formData, setFormData] = useState({
    enabled: true,
    serviceDeskId: defaultServiceDeskId,
    requestTypeId: '',
    windowType: 'month',
    windowDays: '',
    windowFrom: '',
    windowTo: '',
    filters: {
      project: serviceDesk?.config?.projectKey || '',
      requestType: '',
      customFields: []
    },
    limit: '',
    behavior: 'BLOCK',
    message: ''
  });

  // Update serviceDeskId when serviceDesk changes (for non-superadmin)
  React.useEffect(() => {
    if (!isSuperAdmin && serviceDesk?.id) {
      setFormData(prev => ({
        ...prev,
        serviceDeskId: serviceDesk.id.toString(),
        filters: {
          ...prev.filters,
          project: serviceDesk.config?.projectKey || prev.filters.project
        }
      }));
    }
  }, [serviceDesk?.id, isSuperAdmin]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleFiltersChange = (filters) => {
    setFormData(prev => ({ ...prev, filters }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Build window object based on type
      let window;
      switch (formData.windowType) {
        case 'month':
          window = { type: 'month' };
          break;
        case 'isoQuarter':
          window = { type: 'isoQuarter' };
          break;
        case 'rollingDays':
          const days = parseInt(formData.windowDays);
          if (isNaN(days) || days <= 0) {
            throw new Error(t('The number of days must be a positive number'));
          }
          window = { type: 'rollingDays', days };
          break;
        case 'fixed':
          if (!formData.windowFrom || !formData.windowTo) {
            throw new Error(t('Start and end dates are required for a fixed period'));
          }
          window = {
            type: 'fixed',
            from: formData.windowFrom,
            to: formData.windowTo
          };
          break;
        case 'none':
          window = { type: 'none' };
          break;
        default:
          throw new Error(t('Invalid period type'));
      }

      // Parse serviceDeskId and requestTypeId
      const serviceDeskId = parseInt(formData.serviceDeskId);
      const requestTypeId = parseInt(formData.requestTypeId);
      const limit = parseInt(formData.limit);

      if (isNaN(serviceDeskId)) {
        throw new Error(t('Invalid Service Desk ID'));
      }
      if (isNaN(requestTypeId)) {
        throw new Error(t('Invalid Request Type ID'));
      }
      if (isNaN(limit) || limit < 0) {
        throw new Error(t('The limit must be a positive number'));
      }

      // Send filters directly - backend will build JQL
      const payload = {
        enabled: formData.enabled,
        scope: {
          serviceDeskId,
          requestTypeId
        },
        window,
        filters: formData.filters, // Backend will build JQL from this
        limit,
        onExceed: {
          behavior: formData.behavior,
          message: formData.message.trim()
        }
      };

      const response = await fetchWithAuth('/admin/quotas', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || t('Creating the quota failed'));
      }

      if (json.success) {
        // Reset form
        setFormData({
          enabled: true,
          serviceDeskId: defaultServiceDeskId,
          requestTypeId: '',
          windowType: 'month',
          windowDays: '',
          windowFrom: '',
          windowTo: '',
          filters: {
            project: serviceDesk?.config?.projectKey || '',
            requestType: '',
            customFields: []
          },
          limit: '',
          behavior: 'BLOCK',
          message: ''
        });
        onQuotaCreated();
      } else {
        throw new Error(json.error || t('Creating the quota failed'));
      }
    } catch (err) {
      console.error('Error creating quota:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('Create a new quota')}</DialogTitle>
          <DialogDescription>
            {t('Configure a new quota rule for a service desk and request type')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="enabled">{t('Status')}</Label>
            <Select value={formData.enabled.toString()} onValueChange={(value) => handleChange('enabled', value === 'true')}>
              <SelectTrigger id="enabled">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{t('Active')}</SelectItem>
                <SelectItem value="false">{t('Inactive')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="serviceDeskId">{t('Service Desk *')}</Label>
            {isSuperAdmin ? (
              <select
                id="serviceDeskId"
                value={formData.serviceDeskId}
                onChange={(e) => {
                  const sdId = e.target.value;
                  const selectedSd = serviceDesksWithRequestTypes.find(sd => sd.id.toString() === sdId);
                  setFormData(prev => ({
                    ...prev,
                    serviceDeskId: sdId,
                    requestTypeId: '', // Reset request type when service desk changes
                    filters: {
                      ...prev.filters,
                      project: selectedSd?.projectKey || ''
                    }
                  }));
                }}
                className="w-full px-3 py-2 border rounded-md text-sm"
                required
              >
                <option value="">{t('Select a service desk…')}</option>
                {serviceDesksWithRequestTypes.map((sd) => (
                  <option key={sd.id} value={sd.id}>
                    {sd.name} ({sd.projectKey})
                  </option>
                ))}
              </select>
            ) : (
              <Input
                id="serviceDeskId"
                value={serviceDesk?.config?.name ? `${serviceDesk.config.name} (${serviceDesk.config.projectKey})` : ''}
                disabled
                className="bg-gray-100"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="requestTypeId">{t('Request type *')}</Label>
            <select
              id="requestTypeId"
              value={formData.requestTypeId}
              onChange={(e) => handleChange('requestTypeId', e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm"
              required
              disabled={!formData.serviceDeskId}
            >
              <option value="">{t('Select a request type…')}</option>
              {serviceDesksWithRequestTypes
                .find(sd => sd.id.toString() === formData.serviceDeskId)
                ?.requestTypes.map((rt) => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name}
                  </option>
                ))}
            </select>
            {!formData.serviceDeskId && (
              <p className="text-xs text-muted-foreground">
                {t('Select a service desk first')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="windowType">{t('Period type *')}</Label>
            <Select value={formData.windowType} onValueChange={(value) => handleChange('windowType', value)}>
              <SelectTrigger id="windowType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">{t('Monthly')}</SelectItem>
                <SelectItem value="isoQuarter">{t('Quarterly')}</SelectItem>
                <SelectItem value="rollingDays">{t('Rolling days')}</SelectItem>
                <SelectItem value="fixed">{t('Fixed period')}</SelectItem>
                <SelectItem value="none">{t('No limit')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.windowType === 'rollingDays' && (
            <div className="space-y-2">
              <Label htmlFor="windowDays">{t('Number of days *')}</Label>
              <Input
                id="windowDays"
                type="number"
                value={formData.windowDays}
                onChange={(e) => handleChange('windowDays', e.target.value)}
                placeholder={t('Ex: 30')}
                required
              />
            </div>
          )}

          {formData.windowType === 'fixed' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="windowFrom">{t('Start date *')}</Label>
                <Input
                  id="windowFrom"
                  type="date"
                  value={formData.windowFrom}
                  onChange={(e) => handleChange('windowFrom', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="windowTo">{t('End date *')}</Label>
                <Input
                  id="windowTo"
                  type="date"
                  value={formData.windowTo}
                  onChange={(e) => handleChange('windowTo', e.target.value)}
                  required
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('Filters')}</Label>
            <div className="border rounded-lg p-4 bg-gray-50">
              {serviceDesksWithRequestTypes.length > 0 ? (
                <FilterBuilder
                  filters={formData.filters}
                  onChange={handleFiltersChange}
                  serviceDeskConfig={
                    // Use selected service desk's config from loaded data
                    serviceDesksWithRequestTypes.find(sd => sd.id.toString() === formData.serviceDeskId)
                  }
                  hideServiceDeskField={true}
                />
              ) : (
                <div className="text-sm text-gray-500 py-4 text-center">
                  {t('Loading configurations…')}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('Configure the filters for requests subject to the quota')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="limit">{t('Limit *')}</Label>
            <Input
              id="limit"
              type="number"
              value={formData.limit}
              onChange={(e) => handleChange('limit', e.target.value)}
              placeholder={t('Ex: 10')}
              min="0"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="behavior">{t('Action on exceed *')}</Label>
            <Select value={formData.behavior} onValueChange={(value) => handleChange('behavior', value)}>
              <SelectTrigger id="behavior">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BLOCK">{t('Block')}</SelectItem>
                <SelectItem value="WARN">{t('Warn')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t('Message *')}</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder={t('Ex: Monthly quota reached for this request type')}
              rows={2}
              required
            />
            <p className="text-xs text-muted-foreground">
              {t('Message shown to the user when the quota is reached')}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('Create the quota')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
